import React from 'react';
import { useTranslation } from 'react-i18next';
import { Award, CheckCircle, Shield, Users, Leaf, Settings } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

export const Certifications = () => {
  const { t } = useTranslation();

  const certifications = [
    {
      id: 'iso-9001',
      icon: Settings,
      standard: 'ISO 9001',
      color: 'from-blue-500 to-blue-600',
      bgColor: 'from-blue-50 to-blue-100'
    },
    {
      id: 'iso-14001',
      icon: Leaf,
      standard: 'ISO 14001',
      color: 'from-emerald-500 to-emerald-600',
      bgColor: 'from-emerald-50 to-emerald-100'
    },
    {
      id: 'iso-45001',
      icon: Shield,
      standard: 'ISO 45001',
      color: 'from-orange-500 to-orange-600',
      bgColor: 'from-orange-50 to-orange-100'
    },
    {
      id: 'iso-10002',
      icon: Users,
      standard: 'ISO 10002',
      color: 'from-purple-500 to-purple-600',
      bgColor: 'from-purple-50 to-purple-100'
    }
  ];

  return (
    <div className="flex flex-col">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-neutral-900 via-neutral-800 to-emerald-900 text-white py-12 sm:py-16 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center mb-4 sm:mb-6">
            <Award className="text-emerald-400" size={36} />
          </div>
          <span className="inline-block w-full text-center text-emerald-400 font-semibold text-xs sm:text-sm uppercase tracking-wider mb-2 sm:mb-4">
            {t('certifications.subtitle')}
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4 lg:mb-6 text-center">{t('certifications.title')}</h1>
          <p className="text-base sm:text-lg lg:text-xl text-neutral-300 max-w-3xl leading-relaxed mx-auto text-center">
            {t('certifications.description')}
          </p>
        </div>
      </section>

      {/* Certifications Grid */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-b from-white to-neutral-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-12 lg:mb-16">
            {certifications.map((cert) => {
              const IconComponent = cert.icon;
              return (
                <Card key={cert.id} className="overflow-hidden hover:shadow-2xl transition-all duration-300 border-0 shadow-lg card-hover">
                  <CardHeader className={`bg-gradient-to-br ${cert.bgColor} pb-4 sm:pb-6 lg:pb-8`}>
                    <div className="flex items-center space-x-3 sm:space-x-4">
                      <div className={`w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br ${cert.color} flex items-center justify-center shadow-lg`}>
                        <IconComponent className="text-white w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8" />
                      </div>
                      <div>
                        <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-neutral-900">{cert.standard}</CardTitle>
                        <p className="text-xs sm:text-sm text-neutral-600 mt-0.5 sm:mt-1">{t(`certifications.${cert.id}.name`)}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4 sm:pt-6 lg:pt-8 pb-4 sm:pb-6 lg:pb-8">
                    <p className="text-neutral-700 text-xs sm:text-sm leading-relaxed mb-4 sm:mb-6">
                      {t(`certifications.${cert.id}.description`)}
                    </p>
                    <div className="space-y-2 sm:space-y-3">
                      <h4 className="font-semibold text-xs sm:text-sm text-neutral-900 mb-2 sm:mb-3">{t('certifications.keyBenefits')}:</h4>
                      {[1, 2, 3].map((num) => (
                        <div key={num} className="flex items-start">
                          <CheckCircle size={14} className="text-emerald-600 mr-2 sm:mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-xs sm:text-sm text-neutral-600">
                            {t(`certifications.${cert.id}.benefit${num}`)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-100 text-emerald-600 mb-4 sm:mb-6 lg:mb-8 shadow-lg">
              <Award className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900 mb-4 sm:mb-6">{t('certifications.commitment.title')}</h2>
            <p className="text-sm sm:text-base lg:text-lg text-neutral-600 leading-relaxed mb-6 sm:mb-8">
              {t('certifications.commitment.description')}
            </p>
            <div className="grid grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mt-8 sm:mt-10 lg:mt-12">
              <div className="text-center">
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-emerald-600 mb-1 sm:mb-2">{t('certifications.stats.years')}</div>
                <div className="text-neutral-600 text-xs sm:text-sm">{t('certifications.stats.yearsLabel')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-emerald-600 mb-1 sm:mb-2">{t('certifications.stats.audits')}</div>
                <div className="text-neutral-600 text-xs sm:text-sm">{t('certifications.stats.auditsLabel')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-emerald-600 mb-1 sm:mb-2">{t('certifications.stats.standards')}</div>
                <div className="text-neutral-600 text-xs sm:text-sm">{t('certifications.stats.standardsLabel')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-b from-neutral-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900 mb-2 sm:mb-4">{t('certifications.whyMatters.title')}</h2>
            <p className="text-neutral-600 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto">
              {t('certifications.whyMatters.description')}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-6 sm:pt-8 pb-6 sm:pb-8 text-center">
                <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <CheckCircle className="text-emerald-600 w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />
                </div>
                <h3 className="font-semibold text-base sm:text-lg lg:text-xl mb-2 sm:mb-3 text-neutral-900">{t('certifications.whyMatters.quality')}</h3>
                <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed">
                  {t('certifications.whyMatters.qualityDesc')}
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-6 sm:pt-8 pb-6 sm:pb-8 text-center">
                <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <Shield className="text-emerald-600 w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />
                </div>
                <h3 className="font-semibold text-base sm:text-lg lg:text-xl mb-2 sm:mb-3 text-neutral-900">{t('certifications.whyMatters.trust')}</h3>
                <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed">
                  {t('certifications.whyMatters.trustDesc')}
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow sm:col-span-2 md:col-span-1">
              <CardContent className="pt-6 sm:pt-8 pb-6 sm:pb-8 text-center">
                <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <Leaf className="text-emerald-600 w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />
                </div>
                <h3 className="font-semibold text-base sm:text-lg lg:text-xl mb-2 sm:mb-3 text-neutral-900">{t('certifications.whyMatters.sustainability')}</h3>
                <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed">
                  {t('certifications.whyMatters.sustainabilityDesc')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Certifications;